'use client';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import Button from '@/components/atoms/button';
import { Input, InputFieldProps } from '@/components/atoms/input';
import { InputLabel } from '@/components/atoms/input/InputLabel';
import { WithRef } from '@/components/types';

import Popover, { PopoverProps } from '../popover/index';

import IconChevronDown from '~/svg/IconChevronDown.svg';
import IconSearch from '~/svg/IconSearch.svg';
import TrackerClickWrapper from '../TrackerClickWrapper';

export type DropdownOption = {
  label: string;
  value: string;
};

export type InputDropdownProps = {
  options: DropdownOption[];
  onOptionChange: (value: any) => void;
  value?: string;
  hasSearch?: boolean;
  classNames?: {
    root?: string;
    helpText?: string;
    search?: string;
    inputField?: string;
    dropdown?: string;
  };
  popoverProps?: Partial<PopoverProps>;
} & Omit<InputFieldProps, 'classNames'> & {
    targetClassNames?: InputFieldProps['classNames'];
  };

export const InputDropdown = ({
  onOptionChange,
  options: propOptions,
  name,
  value,
  hasSearch,
  ref,
  classNames,
  ...props
}: WithRef<HTMLInputElement, InputDropdownProps>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState(propOptions);

  function handleSelect(value: DropdownOption['value']) {
    onOptionChange(value);
    setIsOpen(false);
  }

  useEffect(() => {
    setOptions(propOptions);
  }, [propOptions]);

  return (
    <div className={cn('relative w-full', classNames?.root)}>
      <div className='align-center relative flex w-full items-center'>
        <Popover
          {...props.popoverProps}
          classNames={{
            ...props.popoverProps?.classNames,
            // TODO mt-1
            target: cn(
              'w-full group/input-dropdown',
              props.popoverProps?.classNames?.target
            ),
            childrenWrapper: cn(
              'border-primary-P99 -mt-2 py-2 px-0 rounded w-full min-w-[140px]',
              props.popoverProps?.classNames?.childrenWrapper
            ),
          }}
          position='bottom'
          showArrow={false}
          onChange={(value) => setIsOpen(value)}
          isOpen={isOpen}
          contentProps={{
            ...props.popoverProps?.contentProps,
            avoidCollisions: false,
          }}
          target={
            <div className='flex w-full flex-col'>
              {props.label && (
                <InputLabel
                  label={props.label}
                  required={props.isRequired}
                  fieldName={name}
                />
              )}
              <Button
                onClick={() => setIsOpen(true)}
                type='button'
                role='presentation'
                tabIndex={0}
                variant='input'
                classNames={{
                  root: 'w-full justify-between mb-4 group-data-[state=open]/input-dropdown:border-primary-P99', // TODO - fix this
                  inner: cn(
                    'h-auto overflow-hidden text-ellipsis inline-block',
                    !value && 'text-neutral-N50'
                  ),
                }}
                rightSection={
                  <IconChevronDown
                    className={cn(
                      'h-6 w-6 transition duration-500 ease-in-out',
                      isOpen &&
                        'text-neutral-N99 dark:text-neutral-dark-N20 rotate-180'
                    )}
                  />
                }
              >
                {!value
                  ? props.placeholder
                  : propOptions.find((option) => option.value === value)
                      ?.label || value}
              </Button>
            </div>
          }
        >
          <div className=''>
            {hasSearch && (
              <div className='px-4 py-1'>
                <Input
                  size='s'
                  name='name of input'
                  placeholder='Search'
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === '') {
                      setOptions(propOptions);
                      return;
                    }
                    setOptions((prevState) =>
                      prevState.filter((option) =>
                        option.label.toLowerCase().includes(value.toLowerCase())
                      )
                    );
                  }}
                  leftIcon={<IconSearch />}
                  classNames={{ bottomText: { root: 'hidden' } }}
                />
              </div>
            )}
            <ul
              className={cn(
                'text-neutral-N50 dark:text-neutral-dark-N20 max-h-80 overflow-y-auto hover:no-underline',
                classNames?.dropdown,
                isOpen ? 'block' : 'hidden'
              )}
            >
              {options.length > 0 ? (
                options.map((option, index) => (
                  <li
                    key={index}
                    className='hover:bg-neutral-N02 hover:dark:bg-neutral-dark-N94 cursor-pointer p-1 px-4 text-base'
                    // onMouseDown={() => handleSelect(option.value)}
                    onClick={() => handleSelect(option.value)}
                  >
                    <TrackerClickWrapper data-tracker-context={option.value}>
                      {option.label}
                    </TrackerClickWrapper>
                  </li>
                ))
              ) : (
                <li className='p-1 px-4 text-base'>No options</li>
              )}
            </ul>
          </div>
        </Popover>
      </div>
    </div>
  );
};
